import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FlaskConical, Star, Send, Phone, MessageSquare, Mail, Play, Square, Eye } from "lucide-react";

const CH = {
  voice: { Icon: Phone, label: "Voice call", color: "#D36B46" },
  sms: { Icon: MessageSquare, label: "Text (SMS)", color: "#3b6ea5" },
  email: { Icon: Mail, label: "Email", color: "#2A4B46" },
};
const fmtWhen = (iso, time) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}${time ? ` · ${time}` : ""}`;
  } catch { return time || ""; }
};

export const BetaPanel = () => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    if (!user?.beta_active) return;
    api.get("/beta/preview").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
    return () => { try { window.speechSynthesis?.cancel(); } catch {} };
  }, [user?.beta_active]);

  if (!user?.beta_active) return null;

  const play = (idx, text) => {
    if (!("speechSynthesis" in window)) return toast.error("Your browser doesn't support voice playback");
    window.speechSynthesis.cancel();
    if (playing === idx) { setPlaying(null); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.onend = () => setPlaying(null);
    u.onerror = () => setPlaying(null);
    setPlaying(idx);
    window.speechSynthesis.speak(u);
  };

  const submit = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try {
      await api.post("/beta/feedback", { rating, comment });
      setSent(true);
      toast.success("Thanks for the feedback!");
    } catch (e) { toast.error("Could not send feedback"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="beta-panel" className="mb-8 border border-[var(--accent)]/40 bg-[var(--accent)]/5 rounded-lg p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 grid place-items-center rounded-lg bg-[var(--accent)]/15"><FlaskConical className="w-4 h-4 text-[var(--accent)]" /></span>
          <div>
            <p className="font-semibold text-sm">You're on the Beta program</p>
            <p className="text-xs text-[var(--text-2)]">{user.beta_days_left != null ? `${user.beta_days_left} day(s) left in your trial · ` : ""}Voice & text run in preview mode — nothing is sent live.</p>
          </div>
        </div>
      </div>

      {/* Preview inbox — exact scripts / messages a tester would receive */}
      <div className="mt-4 border-t border-[var(--accent)]/20 pt-4" data-testid="beta-preview">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="w-4 h-4 text-[var(--accent)]" />
          <p className="text-sm font-semibold">Message preview</p>
        </div>
        <p className="text-xs text-[var(--text-2)] mb-3">This is the exact voice script or text that would go out for your upcoming reminders. Press play to hear the voice call.</p>

        {loading ? (
          <p className="text-xs text-[var(--text-2)] py-4">Loading preview…</p>
        ) : items.length === 0 ? (
          <div data-testid="beta-preview-empty" className="text-xs text-[var(--text-2)] bg-white border border-[var(--line)] rounded p-4">
            No upcoming reminders yet. Create a reminder or service loop with a Voice, SMS or Email channel and it will show up here exactly as it would be delivered.
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((it, idx) => {
              const meta = CH[it.channel] || CH.email;
              const { Icon } = meta;
              const isPlaying = playing === idx;
              return (
                <div key={idx} data-testid={`beta-preview-item-${idx}`} className="bg-white border border-[var(--line)] rounded p-3 flex gap-3">
                  <div className="w-8 h-8 shrink-0 grid place-items-center rounded" style={{ background: meta.color + "1a" }}>
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{it.title || "Reminder"}</span>
                      <span className="text-[10px] font-mono text-[var(--text-2)] shrink-0">{fmtWhen(it.next_run, it.time)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: meta.color, background: meta.color + "1a" }}>{meta.label}</span>
                      <span className="text-[10px] font-mono text-[var(--text-2)] truncate">→ {it.to}</span>
                    </div>
                    <p className="text-xs text-[var(--text)] mt-2 italic border-l-2 pl-2 whitespace-pre-wrap" style={{ borderColor: meta.color }}>"{it.message}"</p>
                    <button
                      data-testid={`beta-preview-play-${idx}`}
                      onClick={() => play(idx, it.message)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-[var(--line)] hover:border-[var(--accent)] transition-colors"
                    >
                      {isPlaying ? <><Square className="w-3 h-3" /> Stop</> : <><Play className="w-3 h-3" /> {it.channel === "voice" ? "Play voice" : "Read aloud"}</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!sent ? (
        <div className="mt-4 border-t border-[var(--accent)]/20 pt-4">
          <p className="text-xs text-[var(--text-2)] mb-2">How's your experience so far?</p>
          <div className="flex items-center gap-1 mb-3" data-testid="beta-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`beta-star-${n}`} onClick={() => setRating(n)} className="p-0.5">
                <Star className={`w-6 h-6 transition-colors ${n <= rating ? "fill-[var(--accent)] text-[var(--accent)]" : "text-[var(--text-2)]"}`} />
              </button>
            ))}
          </div>
          <textarea data-testid="beta-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="What's working, what's confusing?" className="w-full px-3 py-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)] rounded" />
          <button data-testid="beta-feedback-submit" onClick={submit} disabled={busy} className="mt-2 inline-flex items-center gap-2 h-10 px-4 bg-[#1A1A18] text-white text-sm hover:bg-[var(--accent)] transition-colors disabled:opacity-50 rounded">
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send feedback"}
          </button>
        </div>
      ) : (
        <p data-testid="beta-feedback-thanks" className="mt-4 border-t border-[var(--accent)]/20 pt-4 text-sm text-[var(--text-2)]">Thanks! Your feedback helps shape the full release.</p>
      )}
    </div>
  );
};
